import { useCallback, useEffect, useState } from 'react';
import { fetchOrderStockStatus, type OrderStockStatus } from '../api/orderStockStatusApi';
import { fetchOrderWarehouseSummary } from '../api/orderWarehouseApi';
import { fetchOrderWzDocuments } from '../api/orderWzDocumentsApi';
import type { OrderWarehouseSummary } from '../../../types/orderWarehouseSummary';
import type { WZDocumentDto } from '../../../dto/wz';
import { fetchOrderCommunications, type CommunicationLogDto } from '../../notifications/api/communicationsApi';
import { fetchOrderDeliverySchedule, type OrderDeliverySchedule } from '../api/orderDeliveryScheduleApi';

/**
 * Reads behind the ZK card steps (decision 2026-10-04). Each read is the
 * existing owner: stock per line (`/stock-status`, every ZK reader), the
 * warehouse summary with the WP5 plan (`/warehouse-summary`, warehouse
 * readers; the plan only for full-margin readers) and the order's WZ
 * documents (warehouse readers), and the order's notification log (who got
 * which e-mail; the backend filters it per reader) and the trucks with their
 * „Spodziewana data dostarczenia” (`/delivery-schedule`, ADMIN, OPERATIONS,
 * Handlowiec; forecast by delivery 2026-10-16). A failed or forbidden read is `null`
 * (unknown), never an empty list that would read as „nothing issued”.
 */
export interface OrderProcessData {
  stockStatus: OrderStockStatus | null;
  warehouse: OrderWarehouseSummary | null;
  wzDocuments: WZDocumentDto[] | null;
  notifications: CommunicationLogDto[] | null;
  deliverySchedule: OrderDeliverySchedule | null;
  loading: boolean;
}

export function useOrderProcessData(
  orderId: string | undefined,
  options: { canReadWarehouse: boolean; canReadDeliverySchedule?: boolean; refreshKey?: unknown },
) {
  const { canReadWarehouse, canReadDeliverySchedule = false, refreshKey } = options;
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<OrderProcessData & { orderId?: string }>({
    stockStatus: null, warehouse: null, wzDocuments: null, notifications: null, deliverySchedule: null, loading: true,
  });

  useEffect(() => {
    if (!orderId) return undefined;
    let active = true;
    setData((current) => ({ ...current, loading: true }));
    Promise.all([
      fetchOrderStockStatus(orderId).catch(() => null),
      canReadWarehouse ? fetchOrderWarehouseSummary(orderId).catch(() => null) : Promise.resolve(null),
      canReadWarehouse ? fetchOrderWzDocuments(orderId).catch(() => null) : Promise.resolve(null),
      fetchOrderCommunications(orderId).catch(() => null),
      canReadDeliverySchedule ? fetchOrderDeliverySchedule(orderId).catch(() => null) : Promise.resolve(null),
    ]).then(([stockStatus, warehouse, wzDocuments, notifications, deliverySchedule]) => {
      if (active) setData({ orderId, stockStatus, warehouse, wzDocuments, notifications, deliverySchedule, loading: false });
    });
    return () => { active = false; };
  }, [orderId, canReadWarehouse, canReadDeliverySchedule, refreshKey, revision]);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const current = data.orderId === orderId
    ? data
    : { stockStatus: null, warehouse: null, wzDocuments: null, notifications: null, deliverySchedule: null, loading: true };
  return { ...current, refresh };
}
